/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    createContext,
    memo,
    useContext,
    useState,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    type ComponentPropsWithoutRef,
    type ReactElement,
} from 'react';
import { Form, useLocation, useFetcher } from 'react-router';
import { NavLink } from '@/components/link';
import { useNavigate } from '@/hooks/use-navigate';
import { buildCategorySlugPath, getCategoryAlternativeUrl } from '@/lib/category/category-utils';
import type { ShopperProducts } from '@salesforce/storefront-next-runtime/scapi';
import CategoryNavigationMenu, {
    WithCategoryNavigationMenu,
    type CategoryNavigationMenuListCtx,
    type CategoryNavigationMenuListItemCtx,
} from '@/components/navigation-menu';
import { useSubCategory } from '@/components/navigation-menu/context';
import { useAuth } from '@/providers/auth';
import { useModalContext } from '@/contexts/modal-context';
import { Button } from '@/components/ui/button';
import { ChevronRight, Plus, Minus } from 'lucide-react';
import hamburgerIcon from '/images/hamburger-icon.svg';
import insiderLogo from '/images/FJ-insider-footer-icon.svg';
import { useTranslation } from 'react-i18next';
import { toImageUrl, transformHtmlImageUrls } from '@/lib/dynamic-image';
import DynamicImage from '@/components/dynamic-image';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { resourceRoutes } from '@/route-paths';
import { NavigationMenuLink } from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { NavMenuPage } from './nav-menu-page';
import { NavMenuPageMobile } from './nav-menu-page-mobile';
import type { PageWithComponentData } from '@/lib/util/pageLoader.server';
import type { PdPageLoaderResult } from '@/routes/resource.pd-page';
import type { MenuLoaderResult } from '@/routes/resource.menu';
import type { AccountSummary } from '@/routes/resource.account-summary';
import { registry } from '@/lib/page-designer/registry';
import { useMobileMenuState } from '@/components/header';
import { flagUsa, CountryOptionList } from '@/components/country-selector';
import iconMyjoysLogo from '/images/icon-myjoys-logo.svg';
import { extractFooterCountrySelectorContent, type CountryOption } from '@/components/footer';

// Stable module-level object so useCallback closures can reference it without
// adding it as a dependency (its shape never changes).
const DESKTOP_DEFAULT_LIST_STYLE = { width: '100%', maxWidth: '100%' };

/**
 * Walks all Page Designer components referenced in the nav menu pages and calls
 * registry.preload() for each unique typeId. This fires the dynamic import for
 * every component module in the background so they are cached before the user
 * opens any dropdown, preventing the opening animation from being interrupted
 * by a mid-flight import completing and changing the viewport height.
 */
function preloadNavPageComponents(pages: MenuLoaderResult): void {
    const seen = new Set<string>();

    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    function walkRegions(regions: any[]): void {
        for (const region of regions) {
            for (const comp of region.components ?? []) {
                if (comp.typeId && !seen.has(comp.typeId)) {
                    seen.add(comp.typeId);
                    void registry.preload(comp.typeId);
                }
                // Components can have nested regions (e.g. Layout.grid)
                if (comp.regions?.length) walkRegions(comp.regions);
            }
        }
    }

    for (const page of Object.values(pages)) {
        if (page?.regions?.length) walkRegions(page.regions as any[]);
    }
}

interface MobileMenuContextType {
    isOpen: boolean;
    toggle: () => void;
    close: () => void;
    categories: ShopperProducts.schemas['Category'][];
}

const MobileMenuContext = createContext<MobileMenuContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMobileMenu() {
    return useContext(MobileMenuContext);
}

function hasBanner(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    return typeof category?.c_headerMenuBanner === 'string' && category?.c_headerMenuBanner?.length > 0;
}

function isVertical(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    // Default to vertical if not set
    if (!category?.c_headerMenuOrientation) {
        return true;
    }
    // Only horizontal if explicitly set to "horizontal"
    return String(category.c_headerMenuOrientation).toLowerCase() !== 'horizontal';
}

function CategoryBanner({
    category,
    ...props
}: ComponentPropsWithoutRef<'a'> & { category: ShopperProducts.schemas['Category'] }) {
    const config = useConfig<AppConfig>();
    const imageSrc = toImageUrl({ src: (category?.c_slotBannerImage as string) ?? '', config });

    // Transform any image URLs in the HTML banner to use DIS with WebP optimization
    const transformedBannerHtml = transformHtmlImageUrls((category.c_headerMenuBanner as string) || '', config);

    const altUrl = getCategoryAlternativeUrl(category);
    const isContentPage = altUrl && altUrl !== 'javascript:void(0)';

    const bannerContent = imageSrc ? (
        <DynamicImage
            src={imageSrc}
            alt={category.name ?? ''}
            className="w-full max-w-full max-h-[512px]"
            imageProps={{ className: 'w-full h-full' }}
            objectFit="contain"
            widths={['100vw']}
        />
    ) : (
        // eslint-disable-next-line react/no-danger
        <div className="ml-auto" dangerouslySetInnerHTML={{ __html: transformedBannerHtml }} />
    );

    // Use a plain <a> for alternative URLs so the href is not transformed by buildUrl
    if (isContentPage) {
        return (
            <NavigationMenuLink asChild>
                <a {...props} href={altUrl}>
                    {bannerContent}
                </a>
            </NavigationMenuLink>
        );
    }

    return (
        <NavigationMenuLink asChild>
            <NavLink {...props} to={`/category/${buildCategorySlugPath(category.parentCategoryTree, category.id)}`} prefetch="intent">
                {bannerContent}
            </NavLink>
        </NavigationMenuLink>
    );
}

function hasSubcategories(category: ShopperProducts.schemas['Category']): boolean {
    return (
        typeof category.onlineSubCategoriesCount === 'number' &&
        category.onlineSubCategoriesCount > 0 &&
        Array.isArray(category.categories) &&
        category.categories.length > 0
    );
}

/**
 * MobileMenuOverlay — drill-down menu panel shown when the hamburger is tapped.
 *
 * Two views: a root list of top-level categories (each with `>` chevron), and a
 * sub-category page reached by tapping a category that has children. The sub-page
 * shows `‹ Back` / NAME / `View All` and an inline `+`/`−` accordion of its L2 children.
 *
 * The panel is positioned just below the header (`top: var(--header-height)`) so the
 * promo bar and header row — including the close X rendered by `<Header>` in the
 * right slot — stay visible.
 *
 *
 */

const COUNTRIES_PD_FETCHER_KEY = 'pd-page/countries-pd';

function MobileCountrySelector() {
    const { t } = useTranslation('header');
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const fetcher = useFetcher<PdPageLoaderResult>({ key: COUNTRIES_PD_FETCHER_KEY });
    const loadedRef = useRef(false);

    const options: CountryOption[] = fetcher.data
        ? extractFooterCountrySelectorContent(fetcher.data as PageWithComponentData).options
        : [];

    const active = options.find((o) => pathname.startsWith(o.href));
    const shortLabel = active ? (active.label.split(/\s+/).pop() ?? active.label) : 'US';

    const handleToggle = () => {
        if (!loadedRef.current) {
            loadedRef.current = true;
            fetcher.load('/resource/pd-page?pageId=countries-pd');
        }
        setOpen((v) => !v);
    };

    return (
        <div className="relative">
            <button
                type="button"
                onClick={handleToggle}
                className="flex items-center w-full gap-5 cursor-pointer px-[25px]">
                <img src={flagUsa} alt="" aria-hidden="true" className="w-6 h-6 shrink-0 pl-1" />
                <div className="flex items-start w-full text-base leading-[19px] font-thin text-primary-black">
                    {shortLabel ? `${shortLabel}` : ''}
                </div>
            </button>
            {open && (
                <div
                    className="fixed left-[25px] z-50  min-w-[320px] flex flex-col justify-center bg-footer-background border-b-0 border-2 border-[#e7e7e7]"
                    style={{ top: 'var(--header-height)', bottom: '45px' }}>
                    <ul className="overflow-y-auto flex-1">
                        <div className="flex items-center justify-between pl-5 p-2_5 border-b-2 border-[#e7e7e7] shrink-0">
                            <h3 className="text-[15px] text-[#333333] font-bold uppercase leading-[23px] tracking-[0.1em]">
                                {t('changeCountry', 'Change Country')}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                aria-label="Close country selector"
                                className="text-[#D9D9D9] transition-colors">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="23"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <CountryOptionList options={options} activeHref={active?.href} />
                    </ul>
                </div>
            )}
        </div>
    );
}

function MobileHelpCenter() {
    const fetcher = useFetcher<PdPageLoaderResult>();
    const loadedRef = useRef(false);
    const [markup, setMarkup] = useState<string | null>(null);

    useEffect(() => {
        if (loadedRef.current) return;
        loadedRef.current = true;
        fetcher.load('/resource/pd-page?pageId=help-center-faqs-pd');
    }, [fetcher]);

    useEffect(() => {
        if (!fetcher.data) return;
        const page = fetcher.data as PageWithComponentData | null;
        if (!page?.regions) {
            setMarkup(null);
            return;
        }
        for (const region of page.regions) {
            for (const comp of region.components ?? []) {
                const data = comp.data as Record<string, unknown> | undefined;
                if (
                    (comp.typeId === 'Content.markupContent' || comp.typeId === 'Content.contentAsset') &&
                    typeof data?.markup === 'string'
                ) {
                    setMarkup(data.markup);
                    return;
                }
            }
        }
        setMarkup(null);
    }, [fetcher.data]);

    if (!markup) return null;

    return (
        <div
            className="py-[21px] pl-[25px] pr-[5px]"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: markup }}
        />
    );
}

function MobileMenuOverlay({
    categories,
    menuPages,
    isMenuPagesLoading,
}: {
    categories: ShopperProducts.schemas['Category'][];
    menuPages?: MenuLoaderResult;
    isMenuPagesLoading?: boolean;
}): ReactElement | null {
    const { isOpen, close } = useMobileMenuState();
    const { t } = useTranslation('header');
    const [drillCategoryId, setDrillCategoryId] = useState<string | null>(null);
    const [expandedL2, setExpandedL2] = useState<Set<string>>(new Set());
    const auth = useAuth();
    const isLoggedIn = Boolean(auth?.userType === 'registered' && auth?.customerId);
    const config = useConfig<AppConfig>();

    const { openInsiderModal } = useModalContext();

    const accountSummaryFetcher = useFetcher<AccountSummary>();
    const { state: summaryState, data: summaryData, load: loadSummary } = accountSummaryFetcher;
    useEffect(() => {
        if (isLoggedIn && summaryState === 'idle' && !summaryData) {
            void loadSummary('/resource/account-summary');
        }
    }, [isLoggedIn, summaryState, summaryData, loadSummary]);
    const firstName = summaryData?.firstName ?? '';

    // Stable ref so the Escape handler always calls the latest `close` without
    // being a dep on this effect (body scroll is now owned by Header).
    const closeRef = useRef(close);
    closeRef.current = close;

    // Add Escape key handler while open.
    // State (drillCategoryId, expandedL2) is intentionally NOT reset on close so
    // reopening the menu returns the user to the same sub-category they were viewing.
    // Body scroll lock is handled by Header's useEffect on mobileMenuOpen.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeRef.current();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const drillCategory = drillCategoryId ? categories.find((c) => c.id === drillCategoryId) : undefined;

    const toggleL2 = (id: string) => {
        setExpandedL2((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleBack = () => {
        setDrillCategoryId(null);
        setExpandedL2(new Set());
    };

    const handleDrill = (id: string) => {
        const cat = categories.find((c) => c.id === id);
        const firstSub = cat?.categories?.[0];
        setDrillCategoryId(id);
        // Use onlineSubCategoriesCount (available before defer resolves) rather than
        // hasSubcategories() which also checks categories.length and would always be 0
        // at this point since L3 data only arrives via the deferred promise.
        const firstSubHasChildren =
            firstSub && typeof firstSub.onlineSubCategoriesCount === 'number' && firstSub.onlineSubCategoriesCount > 0;
        setExpandedL2(firstSubHasChildren ? new Set([firstSub.id]) : new Set());
    };

    return (
        <>
            <div
                className="lg:hidden fixed left-0 right-0 z-40 border-t border-[#00000026] bg-header-background text-header-foreground overflow-y-auto"
                style={{ top: 'calc(var(--header-height) - 2px)', bottom: 0 }}>
                <nav aria-label={t('mobileNavigation', 'Mobile navigation menu')}>
                    {drillCategory ? (
                        <MobileMenuPageContent
                            menuPages={menuPages}
                            isMenuPagesLoading={isMenuPagesLoading}
                            category={drillCategory}
                            expandedIds={expandedL2}
                            onToggle={toggleL2}
                            onBack={handleBack}
                            onClose={close}
                        />
                    ) : (
                        <EnrichedRootList
                            categories={categories}
                            menuPages={menuPages}
                            onDrill={handleDrill}
                            onClose={close}
                        />
                    )}
                </nav>
                {/* Footer Insider — top level only, hidden when viewing a sub-category */}
                {!drillCategory && (
                    <div>
                        {/* Insider Section */}
                        <div className="px-5 py-6 bg-footer-insider-bg text-background flex items-center justify-between">
                            <img src={insiderLogo} alt="Insider" className="h-6" />

                            <div className="text-sm leading-[17px] tracking-[0.05em] text-primary-foreground font-bold">
                                {isLoggedIn ? (
                                    <div className="flex flex-col items-end">
                                        <span>
                                            {t('helloGreeting')}
                                            {firstName ? ` ${firstName}` : ''}
                                        </span>
                                        <Form method="post" action={resourceRoutes.logout}>
                                            <button
                                                type="submit"
                                                data-test-id="FJ-insider-mobile-signout-button"
                                                className="underline">
                                                {t('signOut')}
                                            </button>
                                        </Form>
                                    </div>
                                ) : (
                                    <>
                                        <button
                                            data-test-id="FJ-insider-mobile-signup-button"
                                            className="underline mr-1 leading-[17px] tracking-[0.05em]"
                                            onClick={() => openInsiderModal('register')}>
                                            {t('signUp')}
                                        </button>
                                        {'/'}
                                        <button
                                            data-test-id="FJ-insider-mobile-signin-button"
                                            className="underline ml-1 leading-[17px] tracking-[0.05em]"
                                            onClick={() => openInsiderModal('login')}>
                                            {t('signIn')}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        <MobileHelpCenter />
                        <MobileCountrySelector />
                    </div>
                )}
            </div>
        </>
    );
}

/**
 * Keeps the third and sixth top-level categories drillable before and after the
 * deferred PD lookup. Those positions are the catalog's Gloves and Fan Shop
 * entries, which have PD pages despite reporting no online subcategories.
 */
function EnrichedRootList({
    categories,
    menuPages,
    onDrill,
    onClose,
}: {
    categories: ShopperProducts.schemas['Category'][];
    menuPages?: MenuLoaderResult;
    onDrill: (id: string) => void;
    onClose: () => void;
}): ReactElement {
    const enriched = (Array.isArray(categories) ? categories : []).map((cat, index) => {
        const hasPdEntry = shouldPromoteToMenuBranch(cat, index, menuPages);
        const subCount = cat.onlineSubCategoriesCount ?? 0;

        if (!hasPdEntry) return cat;
        return subCount < 1 ? { ...cat, onlineSubCategoriesCount: 1 } : cat;
    });
    return <RootList categories={enriched} onDrill={onDrill} onClose={onClose} />;
}

function shouldPromoteToMenuBranch(
    category: ShopperProducts.schemas['Category'],
    index: number,
    menuPages?: MenuLoaderResult
): boolean {
    return index === 2 || index === 5 || Boolean(menuPages?.[category.id]);
}

function RootList({
    categories,
    onDrill,
    onClose,
}: {
    categories: ShopperProducts.schemas['Category'][];
    onDrill: (id: string) => void;
    onClose: () => void;
}): ReactElement {
    const navigate = useNavigate();
    const config = useConfig<AppConfig>();
    return (
        <ul>
            {categories.map((category) => {
                const drillable =
                    typeof category.onlineSubCategoriesCount === 'number' && category.onlineSubCategoriesCount > 0;
                return (
                    <li key={category.id} className="border-b-2  border-[#00000026]">
                        <button
                            type="button"
                            onClick={() => {
                                const altUrl = getCategoryAlternativeUrl(category);
                                const isNavOnly = altUrl === 'javascript:void(0)';
                                if (drillable) {
                                    // Always drill for drillable categories; navigation-only still shows sub-cats
                                    onDrill(category.id);
                                } else if (!isNavOnly) {
                                    onClose();
                                    if (altUrl) {
                                        // Use raw href to avoid buildUrl prepending the site/locale prefix
                                        window.location.href = altUrl;
                                    } else {
                                        void navigate(`/category/${buildCategorySlugPath(category.parentCategoryTree, category.id)}`);
                                    }
                                }
                                // Not drillable + navigation-only: no action
                            }}
                            className="w-full flex items-center justify-between pl-[25px] pr-3 py-[21px] text-left text-[19px] h-[67px] tracking-[0.05em] leading-[24px] font-bold hover:opacity-70 transition-opacity">
                            {(() => {
                                if (category.id === 'myjoys-shoes') {
                                    return (
                                        <img
                                            src={iconMyjoysLogo}
                                            alt={category.name ?? ''}
                                            className="object-none w-[90px] h-[26px] overflow-visible"
                                        />
                                    );
                                }
                                return <span>{category.name}</span>;
                            })()}
                            <ChevronRight
                                className="size-6 shrink-0"
                                strokeWidth="4"
                                strokeLinecap="square"
                                aria-hidden
                            />
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}

/**
 * Shown while the PD page promise is in-flight.
 * Renders only the shared header row so the user sees the correct category name
 * without any flash of Commerce-API accordion data.
 */
function SubCategoryLoadingView({
    category,
    onBack,
}: {
    category: ShopperProducts.schemas['Category'];
    onBack: () => void;
}): ReactElement {
    const { t } = useTranslation('header');
    return (
        <div>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center px-5 py-[11px] border-b border-[#00000026]">
                <button
                    type="button"
                    onClick={onBack}
                    className="justify-self-start text-base text-[#808080] leading-[30px] font-medium hover:opacity-70 transition-opacity">
                    {`< ${t('back', 'Back')}`}
                </button>
                <h2 className="justify-self-center text-2xl font-bold text-[#1c1e29] uppercase tracking-wide">
                    {category.name}
                </h2>
                <span />
            </div>
        </div>
    );
}

/**
 * Shows NavMenuPageMobile when a resolved PD page exists for this category,
 * falls back to SubCategoryView (Commerce API accordion) otherwise. While the
 * aggregate fetch is still in flight, shows the header-only loading skeleton
 * instead of flashing the fallback content before swapping to PD content.
 */
function MobileMenuPageContent({
    menuPages,
    isMenuPagesLoading,
    category,
    expandedIds,
    onToggle,
    onBack,
    onClose,
}: {
    menuPages?: MenuLoaderResult;
    isMenuPagesLoading?: boolean;
    category: ShopperProducts.schemas['Category'];
    expandedIds: Set<string>;
    onToggle: (id: string) => void;
    onBack: () => void;
    onClose: () => void;
}): ReactElement {
    const fallback = (
        <SubCategoryView
            category={category}
            expandedIds={expandedIds}
            onToggle={onToggle}
            onBack={onBack}
            onClose={onClose}
        />
    );

    if (!menuPages) {
        return isMenuPagesLoading ? <SubCategoryLoadingView category={category} onBack={onBack} /> : fallback;
    }

    const page = menuPages[category.id];
    if (page === undefined) return fallback;
    return page ? (
        <SubCategoryPDView page={page} category={category} onBack={onBack} onClose={onClose} />
    ) : (
        fallback
    );
}

/**
 * Shared header (‹ Back | CATEGORY | View All) + the mobile PD content body.
 * Mirrors the header in SubCategoryView so both paths look identical.
 */
function SubCategoryPDView({
    page,
    category,
    onBack,
    onClose,
}: {
    page: PageWithComponentData;
    category: ShopperProducts.schemas['Category'];
    onBack: () => void;
    onClose: () => void;
}): ReactElement {
    const { t } = useTranslation('header');
    // null until NavMenuPageMobile finds a .level-2__category__view-all__link in the PD
    // markup (present on Men/Women only). Stays null for other categories so the button
    // is hidden.
    const [viewAllUrl, setViewAllUrl] = useState<string | null>(null);

    return (
        <div>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center px-5 py-[11px] border-b border-[#00000026]">
                <button
                    type="button"
                    onClick={onBack}
                    className="justify-self-start text-base text-[#808080] leading-[30px] font-medium hover:opacity-70 transition-opacity">
                    {`< ${t('back', 'Back')}`}
                </button>
                <h2 className="justify-self-center text-2xl font-bold text-[#1c1e29] uppercase tracking-wide">
                    {category.name}
                </h2>
                {viewAllUrl ? (
                    <a
                        href={viewAllUrl}
                        onClick={onClose}
                        className="justify-self-end text-base text-[#1c1e29] font-bold underline hover:opacity-70 transition-opacity">
                        {t('viewAll', 'View All')}
                    </a>
                ) : (
                    <span />
                )}
            </div>
            <NavMenuPageMobile page={page} onClose={onClose} onViewAllUrl={setViewAllUrl} />
        </div>
    );
}

function SubCategoryView({
    category,
    expandedIds,
    onToggle,
    onBack,
    onClose,
}: {
    category: ShopperProducts.schemas['Category'];
    expandedIds: Set<string>;
    onToggle: (id: string) => void;
    onBack: () => void;
    onClose: () => void;
}): ReactElement {
    const { t } = useTranslation('header');
    // Use the enriched (deferred) subcategory data so L3 children are populated,
    // exactly as the desktop does in impl.tsx via useSubCategory.
    const enriched = useSubCategory(category.id) ?? category;
    // Use Array.isArray instead of ?? [] — nullish coalescing passes truthy non-arrays
    // (e.g. the empty-object `{}` SCAPI returns for leaf categories) through unchanged,
    // which makes .map() throw "is not a function" at line 732.
    const subCategories = Array.isArray(enriched.categories) ? enriched.categories : [];
    const parentPath = buildCategorySlugPath(category.parentCategoryTree, category.id);

    return (
        <div>
            {/* Sub-page header: ‹ Back | NAME | View All */}
            <div className="grid grid-cols-[1fr_auto_1fr] items-center px-5 py-[11px] border-b border-[#00000026]">
                <button
                    type="button"
                    onClick={onBack}
                    className="justify-self-start text-base text-[#808080] leading-[30px] font-medium hover:opacity-70 transition-opacity">
                    {`< ${t('back', 'Back')}`}
                </button>
                <h2 className="justify-self-center text-2xl font-bold text-[#1c1e29] uppercase tracking-wide">
                    {category.name}
                </h2>
                {/* <NavLink
                    to={`/category/${category.id}`}
                    onClick={onClose}
                    className="justify-self-end text-base text-[#1c1e29] font-bold underline hover:opacity-70 transition-opacity">
                    {t('viewAll', 'View All')}
                </NavLink> */}
            </div>

            {/* L2 list with +/− accordion for any sub that has L3 children */}
            <ul>
                {subCategories.map((sub) => {
                    // Mirror desktop impl.tsx hasChildren: use onlineSubCategoriesCount only,
                    // not categories.length, so accordion buttons appear before defer resolves.
                    const expandable =
                        typeof sub.onlineSubCategoriesCount === 'number' && sub.onlineSubCategoriesCount > 0;
                    const isExpanded = expandedIds.has(sub.id);
                    return (
                        <li key={sub.id} className="border-b border-header-divider">
                            <div className="flex items-center justify-between">
                                {expandable ? (
                                    <button
                                        type="button"
                                        onClick={() => onToggle(sub.id)}
                                        aria-expanded={isExpanded}
                                        aria-label={
                                            isExpanded
                                                ? t('collapseCategory', `Collapse ${sub.name}`, { category: sub.name })
                                                : t('expandCategory', `Expand ${sub.name}`, { category: sub.name })
                                        }
                                        className="flex-1 flex items-center justify-between px-4 py-5 text-left text-base font-semibold hover:opacity-70 transition-opacity">
                                        <span>{sub.name}</span>
                                        {isExpanded ? (
                                            <Minus className="size-5 shrink-0" aria-hidden />
                                        ) : (
                                            <Plus className="size-5 shrink-0" aria-hidden />
                                        )}
                                    </button>
                                ) : (
                                    <NavLink
                                        to={`/category/${parentPath}/${sub.id}`}
                                        prefetch="intent"
                                        onClick={onClose}
                                        className="flex-1 px-4 py-5 text-base font-semibold hover:opacity-70 transition-opacity">
                                        {sub.name}
                                    </NavLink>
                                )}
                            </div>

                            {expandable && isExpanded && (
                                <ul className="pb-3">
                                    {Array.isArray(sub.categories) && sub.categories.map((leaf) => (
                                        <li key={leaf.id}>
                                            <NavLink
                                                to={`/category/${parentPath}/${sub.id}/${leaf.id}`}
                                                prefetch="intent"
                                                onClick={onClose}
                                                className="block px-4 py-2 text-sm hover:opacity-70 transition-opacity">
                                                {leaf.name}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

/**
 * Renders the catalog-feed subcategory list. Used as a fallback when the
 * Page Designer page for a category is missing, fails to load, or resolves empty.
 * Uses the enriched (deferred) subcategory data when available, otherwise falls
 * back to the categories already on the root category payload.
 */
function SubcategoryListFallback({ category }: { category: ShopperProducts.schemas['Category'] }) {
    const enriched = useSubCategory(category.id) ?? category;
    const subs = enriched.categories ?? [];
    if (!subs.length) return null;
    const parentPath = buildCategorySlugPath(category.parentCategoryTree, category.id);
    return (
        <ul className="flex flex-col gap-0 p-4">
            {subs.map((sub) => (
                <li key={sub.id}>
                    <NavigationMenuLink asChild>
                        <NavLink
                            to={`/category/${parentPath}/${sub.id}`}
                            prefetch="intent"
                            className="block py-2 px-3 text-sm font-medium hover:opacity-70 transition-opacity">
                            {sub.name}
                        </NavLink>
                    </NavigationMenuLink>
                </li>
            ))}
        </ul>
    );
}

/**
 * Desktop-only nav that promotes leaf categories to branches (when a Page Designer
 * entry exists) and renders the Radix NavigationMenu structure.
 *
 * Wrapped in `memo` so that parent state changes that don't affect the nav structure
 * (e.g. `isMenuPagesLoading` toggling on the first hover) do not cause the Radix
 * component tree to re-render. A re-render while the user's pointer is over a trigger
 * clears Radix's internal hover-state tracking, which prevents the dropdown from
 * opening. The `renderContent` slot is passed in from the parent as a ref-based stable
 * callback so it always reads the latest `menuPages` / `isMenuPagesLoading` without
 * needing to be recreated on every parent render.
 */
const DesktopNavigationWithPages = memo(function DesktopNavigationWithPages({
    categories,
    menuPages,
    ...categoryNavProps
}: {
    categories: ShopperProducts.schemas['Category'][];
    menuPages?: MenuLoaderResult;
} & Omit<ComponentPropsWithoutRef<typeof CategoryNavigationMenu>, 'categories'>) {
    // Categories whose catalog feed reports `onlineSubCategoriesCount = 0` (e.g. Gloves,
    // Fan Shop) render as leaf links by default and never open a dropdown. When a
    // category has a resolved Page Designer entry in `menuPages`, promote it to "branch" so
    // the hover trigger renders and the PD content can show.
    const enrichedCategories = useMemo(
        () =>
            (Array.isArray(categories) ? categories : []).map((cat, index) => {
                const hasPdEntry = shouldPromoteToMenuBranch(cat, index, menuPages);
                const subCount = cat.onlineSubCategoriesCount ?? 0;
                if (hasPdEntry && subCount < 1) {
                    return { ...cat, onlineSubCategoriesCount: 1 };
                }
                return cat;
            }),
        [categories, menuPages]
    );

    return <CategoryNavigationMenu categories={enrichedCategories} {...categoryNavProps} />;
});

/**
 * ResponsiveNavigationMenu - A unified responsive navigation component
 *
 * This component uses CSS and Tailwind to adapt the same navigation structure
 * for both mobile and desktop:
 * - On mobile (< 1024px): Hamburger button with expandable vertical menu
 * - On desktop (>= 1024px): Horizontal mega menu with dropdown navigation
 *
 * The component renders a single navigation structure with responsive classes
 * controlling layout, visibility, and interaction patterns. This minimizes DOM bloat
 * while maintaining SSR compatibility.
 *
 * @param props - Component props
 * @param props.resolve - Promise resolving to root categories and first-level subcategories
 * @param props.defer - Promise resolving to deeper subcategory data for prefetch
 * @returns A responsive navigation component with CSS-controlled responsive behavior
 */
export default function ResponsiveNavigationMenu({
    resolve,
    defer,
    menuPages: menuPagesPromise,
}: ComponentPropsWithoutRef<typeof WithCategoryNavigationMenu> & {
    menuPages?: Promise<MenuLoaderResult>;
}): ReactElement {
    const mobileMenu = useMobileMenuState();
    const { t } = useTranslation('header');
    const navigate = useNavigate();
    const config = useConfig<AppConfig>();
    const [desktopMenuValue, setDesktopMenuValue] = useState('');
    const closeDesktopMenu = useCallback(() => setDesktopMenuValue(''), []);

    // Resolve the server-provided menu pages promise locally.
    // Using a plain useState/useEffect (not useFetcher) means the state update
    // when data arrives is a normal React update — no React Router useSyncExternalStore
    // blast that would tear through the <Await> subtree and flash the skeleton fallback.
    const [menuPages, setMenuPages] = useState<MenuLoaderResult | undefined>();
    const isMenuPagesLoading = menuPages === undefined;
    useEffect(() => {
        void menuPagesPromise?.then((data) => {
            setMenuPages(data);
            // Eagerly preload all PD component modules referenced in nav dropdowns so
            // they are already cached when the user first opens a dropdown.
            preloadNavPageComponents(data);
        });
    }, [menuPagesPromise]);

    // Keep refs always current so the stable renderMenuContent callback can read the
    // latest values without needing to be recreated when loading state changes.
    const menuPagesRef = useRef(menuPages);
    const isMenuPagesLoadingRef = useRef(isMenuPagesLoading);
    menuPagesRef.current = menuPages;
    isMenuPagesLoadingRef.current = isMenuPagesLoading;

    // Stable renderContent for DesktopNavigationWithPages — reads from refs so
    // DesktopNavigationWithPages (memo'd) doesn't re-render when loading state changes.
    const renderMenuContent = useCallback(
        ({ category }: CategoryNavigationMenuListItemCtx) => {
            const mp = menuPagesRef.current;
            const isLoading = isMenuPagesLoadingRef.current;
            const fallback = <SubcategoryListFallback category={category} />;
            if (!mp) {
                return isLoading ? <div className="h-64 animate-pulse !bg-[#F0F0F0]" /> : fallback;
            }
            const page = mp[category.id];
            if (page === undefined) return undefined;
            return page ? (
                <NavMenuPage
                    page={page}
                    categoryName={category.name}
                    categoryId={category.id}
                    categoryImage={category.c_topNavCategoryImage as string | undefined}
                    fallbackImage={category.id === 'myjoys-shoes' ? iconMyjoysLogo : undefined}
                    onClose={closeDesktopMenu}
                />
            ) : (
                fallback
            );
        },
        [closeDesktopMenu]
    );

    // Stable prop callbacks — all hardcoded values, no external deps.
    // Required so React.memo on DesktopNavigationWithPages can skip re-renders.
    const propsViewportStable = useCallback(
        () => ({
            className: 'rounded-none border-0 will-change-[clip-path,opacity] overflow-hidden',
            style: {
                position: 'fixed' as const,
                top: 'var(--header-height)',
                left: 0,
                width: '100%',
                maxWidth: '100%',
                maxHeight: 'min(700px, calc(100vh - var(--header-height)))',
                backgroundColor: '#ffffff',
                color: '#000000',
                boxShadow: '0 3px 3px 2px rgba(51, 51, 51, 0.3)',
                fontFamily: 'var(--font-sans)',
                fontSize: '0.75rem',
            },
        }),
        []
    );

    const propsContentContainerStable = useCallback(
        () => ({
            className: '!left-auto !right-auto !w-full md:!w-full !animate-none !transition-none',
            style: {
                width: '100%',
                boxSizing: 'border-box' as const,
                margin: 0,
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 106px)',
                paddingTop: 0,
                paddingBottom: 0,
                paddingRight: 0,
                paddingLeft: '76px',
            },
        }),
        []
    );

    const propsContentStable = useCallback(() => ({ className: 'w-full' }), []);

    const propsListStable = useCallback(
        ({ parent, categories: subCategories, level }: CategoryNavigationMenuListCtx) => {
            if (level === 0) {
                return {
                    className:
                        'flex justify-center items-center pt-[5px] px-0 pb-0 m-0 h-[75px] uppercase tracking-normal w-full',
                    style: {
                        fontFamily: '"neuzeit-grotesk", sans-serif',
                        fontSize: '16px',
                        fontWeight: 700,
                        color: '#000',
                        width: '100%',
                    },
                };
            }
            if (level === 1) {
                if (isVertical(parent)) {
                    return {
                        style: DESKTOP_DEFAULT_LIST_STYLE,
                        className: 'flex flex-col gap-0 p-0',
                    };
                }
                return {
                    style: {
                        ...DESKTOP_DEFAULT_LIST_STYLE,
                        gridTemplateColumns: `repeat(${subCategories.length}, minmax(0, 1fr))`,
                    },
                    className: 'grid p-0',
                };
            }
        },
        []
    );

    const renderElementStable = useCallback(({ category, level }: CategoryNavigationMenuListItemCtx) => {
        if (level !== 0) return undefined;
        if (category.id === 'myjoys-shoes') {
            // Same active indicator as the text items below: a transparent bottom
            // border that turns black while this menu is open. `flex items-start`
            // keeps the logo positioned exactly as when it was a direct flex child.
            return (
                <span className="relative flex h-full w-[81px] self-stretch items-center justify-center">
                    {/* The SVG is a 120x40 box whose visible logo is ~81px wide, starting 15px
                        from the left. Crop to exactly that so the wrapper (and the indicator
                        bar, which spans the wrapper) matches the logo width like the text items. */}
                    <img
                        src={iconMyjoysLogo}
                        alt={category.name ?? ''}
                        className="relative object-none w-[81px] h-10 max-w-none -top-[6px]"
                        style={{ objectPosition: '-15px 0' }}
                    />
                    {/* Active indicator: drawn as its own bar (not a border) so it is
                        never clipped and always sits on the bottom edge of the nav.
                        Exactly the logo width up to 1499px (100% zoom = 1277px, 90% = 1413px);
                        from 1500px up (80% = 1590px and below) it extends half the 48px gap on each side, like
                        the text items' indicators. Visual only (negative inset), so item
                        spacing is unchanged. */}
                    <span
                        aria-hidden="true"
                        className="absolute bottom-0 left-0 right-0 h-[8px] bg-transparent group-data-[state=open]:!bg-black [@media(min-width:1500px)]:-left-6 [@media(min-width:1500px)]:-right-6"
                    />
                </span>
            );
        }
        return (
            // Indicator = bottom border. Matches the text width up to 1499px (100% zoom = 1277px,
            // 90% = 1413px). From 1500px up (80% = 1590px and below) the padding widens the border
            // 24px per side and the equal negative margin cancels it in layout, so the bar covers
            // half the 48px gap each side without moving anything.
            // Keep in sync with the MyJoys indicator above.
            <span className="border-b-[10px] border-b-transparent group-data-[state=open]:!border-b-black [@media(min-width:1500px)]:-mx-6 [@media(min-width:1500px)]:px-6">
                {category.name}
            </span>
        );
    }, []);

    const renderSlotListAfterStable = useCallback(({ level, parent }: CategoryNavigationMenuListCtx) => {
        if (level === 1 && hasBanner(parent)) {
            return (
                <aside className="self-stretch">
                    <CategoryBanner category={parent} />
                </aside>
            );
        }
    }, []);

    // Keep navigate in a ref so handleTopLevelClick (and thus getElementProps) can
    // use an empty-deps useCallback. React Router recreates the navigate function
    // on every router-context update (including fetcher state changes), which would
    // cascade into getElementProps changing and break the React.memo protection on
    // DesktopNavigationWithPages, causing a re-render that resets Radix's hover state.
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    // Handler for top-level category clicks
    const handleTopLevelClick = useCallback(
        (category: ShopperProducts.schemas['Category']) => {
            const altUrl = getCategoryAlternativeUrl(category);
            if (altUrl === 'javascript:void(0)') return; // navigation only — let dropdown open, don't navigate
            if (altUrl) {
                // Use raw href to avoid buildUrl prepending the site/locale prefix
                window.location.href = altUrl;
                return;
            }
            void navigateRef.current(`/category/${category.id}`);
        },
        [] // navigate read from ref — stable across router-context updates
    );

    // Element props generator
    const getElementProps = useCallback(
        ({
            level,
            category,
            isLeaf,
        }: {
            level: number;
            category: ShopperProducts.schemas['Category'];
            isLeaf?: boolean;
        }) => {
            const isSubcategory = level >= 1;
            const isTopLevel = level === 0;
            const isClickableParent = level === 0 && !isLeaf && category.id;

            return {
                className: cn(
                    // Top-level header items (MEN, WOMEN, GLOVES…): 16px / 700 /
                    // uppercase, full header height, padding 5px 25px 0 (prod parity),
                    // with the active underline indicator (4.67px bottom border, black
                    // when the menu is open).
                    // !px-[0px]
                    isTopLevel &&
                        'uppercase !rounded-none !bg-transparent hover:!bg-transparent hover:!text-black focus:!bg-transparent focus:!text-black !h-[75px] !pt-[5px] px-0 xl:px-0 !pb-0 !m-0 !float-none !flex',
                    isSubcategory &&
                        'text-sm font-medium hover:!bg-transparent focus:!bg-transparent hover:!text-header-menu-foreground/60 focus:!text-header-menu-foreground/60 transition-colors'
                ),
                style: isTopLevel
                    ? {
                          display: 'flex',
                          float: 'none',
                          fontFamily: 'neuzeit-grotesk, sans-serif',
                          fontSize: '16px',
                          fontWeight: 700,
                          height: '75px',
                          letterSpacing: 'normal',
                          lineHeight: '65px',
                          paddingTop: '5px',
                          margin: 0,
                          justifyContent: 'space-evenly',
                      }
                    : undefined,
                ...(isClickableParent && {
                    // Use onPointerDown instead of onClick for mouse-only navigation.
                    // This preserves keyboard accessibility: Enter/Space on the trigger
                    // expands the dropdown (Radix behavior), while mouse clicks navigate
                    // to the category page. Without this guard, keyboard users would be
                    // forced to navigate without being able to explore subcategories.
                    onPointerDown: (e: React.PointerEvent) => {
                        if (e.pointerType === 'mouse') {
                            handleTopLevelClick(category);
                        }
                    },
                }),
            };
        },
        [handleTopLevelClick]
    );

    return (
        <WithCategoryNavigationMenu
            resolve={resolve}
            defer={defer}
            fallback={
                <div
                    data-testid="desktop-nav"
                    className="flex h-12 items-center lg:h-[75px]">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={mobileMenu.toggle}
                        className="size-12 border-r !border-[#e6e6e6] hover:bg-transparent hover:opacity-50 transition-opacity lg:hidden"
                        aria-label={t('openMenu', 'Open menu')}
                        aria-expanded={mobileMenu.isOpen}>
                        <img src={hamburgerIcon} alt={t('openMenu', 'Open menu')} className="size-8" />
                    </Button>
                    <div
                        data-testid="desktop-nav-loading"
                        className="hidden h-full w-full items-center justify-center gap-10 lg:flex"
                        aria-hidden="true">
                        {[44, 58, 48, 64, 52].map((width) => (
                            <span key={width} className="h-4 !bg-[#F0F0F0] animate-pulse bg-[#e6e6e6]" style={{ width }} />
                        ))}
                    </div>
                </div>
            }>
            {({ categories }) => {
                return (
                    <>
                        {/* Mobile: Hamburger button — hidden when menu is open so only the
                            close X (rendered by <Header> in the right slot) is visible. */}
                        {!mobileMenu.isOpen && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={mobileMenu.toggle}
                                className="size-12 lg:hidden border-r !border-[#e6e6e6] hover:bg-transparent hover:opacity-50 transition-opacity"
                                aria-label={t('openMenu', 'Open menu')}
                                aria-expanded={mobileMenu.isOpen}>
                                {/* <Menu className="size-5" /> */}
                                <img
                                    src={hamburgerIcon}
                                    alt={t('openMenu', 'Open menu')}
                                    className="size-8"
                                    data-testid="hamburger-icon"
                                />
                            </Button>
                        )}

                        {/* Desktop: Mega menu (always rendered, hidden on mobile with CSS) */}
                        <div
                            data-testid="desktop-nav"
                            className="hidden lg:flex items-center justify-center h-[75px] w-full">
                            <DesktopNavigationWithPages
                                categories={categories}
                                className="h-full"
                                menuPages={menuPages}
                                value={desktopMenuValue}
                                onValueChange={setDesktopMenuValue}
                                delayDuration={0}
                                propsViewport={propsViewportStable}
                                propsContentContainer={propsContentContainerStable}
                                propsContent={propsContentStable}
                                propsList={propsListStable}
                                propsElement={getElementProps}
                                renderElement={renderElementStable}
                                renderContent={renderMenuContent}
                                renderSlotListAfter={renderSlotListAfterStable}
                            />
                        </div>

                        {/* Mobile: drill-down panel below the header */}
                        <MobileMenuOverlay
                            categories={categories}
                            menuPages={menuPages}
                            isMenuPagesLoading={isMenuPagesLoading}
                        />
                    </>
                );
            }}
        </WithCategoryNavigationMenu>
    );
}
